import { useEffect, useState, useMemo } from "react";
import { ArrowUpToLine, Loader2, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * 2026-10-07 · 批量把报价单上多个产品快照里的「新规格」价格追加到产品库 price_lists。
 * 规则（已和用户确认）：
 *   • 同 size 已存在 → 跳过
 *   • 可选「同时更新产品主字段」（中文名/材料/规格/起订量，空值不覆盖）
 *   • 不管快照里 selected 的状态，整个产品的所有价格行都考虑
 *   • 仅 admin / export 看得到入口
 */
export default function BackfillPricesDialog({ open, onOpenChange, quote, onDone }) {
  const items = quote?.items || [];
  const [selected, setSelected] = useState({});   // item_number -> bool
  const [updateFields, setUpdateFields] = useState(false);
  const [replaceAll, setReplaceAll] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return;
    // 默认把所有产品都勾上
    const init = {};
    for (const it of items) init[it.item_number] = true;
    setSelected(init);
    setUpdateFields(false);
    setReplaceAll(false);
    setRunning(false);
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const picked = useMemo(() => items.filter((it) => selected[it.item_number]), [items, selected]);
  const toggleAll = (v) => {
    const next = {};
    for (const it of items) next[it.item_number] = v;
    setSelected(next);
  };

  const run = async () => {
    if (!picked.length) { toast.error("请至少勾选一个产品"); return; }
    if (replaceAll) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `确认清空后替换？\n\n将删除 ${picked.length} 个产品在产品库里的所有旧价格行，` +
        `然后用报价单里的价格行全量写入。此操作不可撤销。`
      );
      if (!ok) return;
    }
    setRunning(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/backfill-prices`, {
        item_numbers: picked.map((p) => p.item_number),
        update_product_fields: updateFields,
        replace_all: replaceAll,
      });
      const t = data?.totals || {};
      const parts = [];
      if (t.deleted) parts.push(`删除旧价格 ${t.deleted} 条`);
      if (t.appended) parts.push(`写入 ${t.appended} 条`);
      if (t.skipped) parts.push(`跳过已存在 ${t.skipped} 条`);
      if (t.products_updated) parts.push(`更新 ${t.products_updated} 个产品主字段`);
      if (t.products_missing) parts.push(`${t.products_missing} 个产品不存在`);
      toast.success(parts.join("，") || "已完成");
      onDone?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "回填失败");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="backfill-prices-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpToLine size={16} className="text-brand" /> 回填价格到产品库
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            把勾选产品里<strong>新规格</strong>的价格行追加到对应产品的价格表（同规格已存在则跳过）。
          </DialogDescription>
        </DialogHeader>

        <div className="border-y border-border py-2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={picked.length === items.length && items.length > 0}
                onCheckedChange={(v) => toggleAll(!!v)}
                data-testid="backfill-select-all"
              />
              <span>全选</span>
              <span className="text-muted-foreground">({picked.length} / {items.length})</span>
            </div>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={updateFields} onCheckedChange={(v) => setUpdateFields(!!v)}
                data-testid="backfill-update-fields" />
              <span>同时更新产品主字段 <span className="text-muted-foreground">(中文名/材料/规格/起订量)</span></span>
            </label>
          </div>
          {/* 2026-10-07 · 清空后替换 — 危险操作，默认关 */}
          <label className={`flex items-center gap-1.5 text-xs cursor-pointer rounded-md px-2 py-1.5 transition-colors ${
              replaceAll ? "bg-red-50 border border-red-300 text-red-700" : "border border-transparent"
            }`}>
            <Checkbox checked={replaceAll} onCheckedChange={(v) => setReplaceAll(!!v)}
              data-testid="backfill-replace-all" />
            <span>
              清空后替换 <span className="text-muted-foreground">(先删掉产品库该产品的所有旧价格行，再写入报价单的价格行)</span>
              {replaceAll && <strong className="ml-1">⚠ 不可撤销</strong>}
            </span>
          </label>
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-10">报价单里暂无产品。</p>
          )}
          {items.map((it) => (
            <label key={it.item_number} data-testid={`backfill-row-${it.item_number}`}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-secondary/40 cursor-pointer">
              <Checkbox
                checked={!!selected[it.item_number]}
                onCheckedChange={(v) => setSelected((s) => ({ ...s, [it.item_number]: !!v }))}
              />
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm font-semibold truncate">{it.item_number}</div>
                <p className="text-[11px] text-muted-foreground line-clamp-1">
                  {it.description_zh || it.description || "-"}
                </p>
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0">
                {(it.price_rows || []).length} 个规格
              </span>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running} className="rounded-md" data-testid="backfill-cancel">
            取消
          </Button>
          <Button onClick={run} disabled={running || !picked.length} className="rounded-md bg-brand hover:bg-brand-hover" data-testid="backfill-run">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} 回填 {picked.length ? `(${picked.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
